import {match} from 'ts-pattern';

import {useRef, useState} from 'react';

import {equals, isNil} from 'ramda';

import {
  FinancialProductPreferenceCoordinatesSchema,
  FinancialProductPreferenceRefusalSchema,
  type FinancialProductPreferenceCoordinates,
  type FinancialProductsResponseBody,
  type FinancialProductType,
  useUpdateBusinessCaseFinancialProductPreferenceMutation,
} from '@dms/api/sales';

import {useLatest, useOnMount} from 'shared/web';

import {
  getConfiguredFinancialProducts,
  getDisplayedFinancialProductQuote,
  getFinancialProductOffer,
  getPrimaryFinancialProductType,
  toFinancialProductPreferenceCoordinates,
  toFinancialProductPreferenceRequestBody,
  type DisplayedFinancialProductQuote,
} from 'features/financial-products';

import {FINANCIAL_PRODUCT_ORDER} from './FINANCIAL_PRODUCT_ORDER';

type ProductDrafts = Partial<Record<FinancialProductType, FinancialProductPreferenceCoordinates>>;

const NO_DISPLAYED_QUOTE: DisplayedFinancialProductQuote = {source: 'NONE'};

function draftsFromProducts(products: FinancialProductsResponseBody): ProductDrafts {
  const drafts: ProductDrafts = {};
  for (const type of getConfiguredFinancialProducts(products, FINANCIAL_PRODUCT_ORDER)) {
    const offer = getFinancialProductOffer(products, type);
    if (!isNil(offer)) {
      const parsed = FinancialProductPreferenceCoordinatesSchema.safeParse(
        toFinancialProductPreferenceCoordinates(offer, {fallbackSelection: offer.calculation})
      );
      if (parsed.success) {
        drafts[type] = parsed.data;
      }
    }
  }
  return drafts;
}

interface EditorState {
  baseline: FinancialProductsResponseBody;
  selectedProduct: FinancialProductType | undefined;
  drafts: ProductDrafts;
  saveError: 'conflict' | 'failed' | null;
  isTerminal: boolean;
}

interface FinancialProductPreferenceEditorProps {
  businessCaseId: string;
  products: FinancialProductsResponseBody;
  canUpdate: boolean;
  onClose: () => void;
}

export function useFinancialProductPreferenceEditor(props: FinancialProductPreferenceEditorProps) {
  // Polling may revoke write access, but must not replace the open draft's revision.
  const [state, setState] = useState<EditorState>(() => ({
    baseline: props.products,
    selectedProduct: getPrimaryFinancialProductType(props.products, FINANCIAL_PRODUCT_ORDER),
    drafts: draftsFromProducts(props.products),
    saveError: null,
    isTerminal: false,
  }));
  const stateRef = useRef(state);
  const mounted = useRef(true);
  const saveInFlight = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [updatePreference] = useUpdateBusinessCaseFinancialProductPreferenceMutation();
  const isWritable =
    props.canUpdate &&
    props.products.mode !== 'CONTRACT_SNAPSHOT' &&
    state.baseline.mode !== 'CONTRACT_SNAPSHOT' &&
    !state.isTerminal;
  const writableRef = useLatest(isWritable);

  const updateState = (next: EditorState) => {
    stateRef.current = next;
    setState(next);
  };

  useOnMount(
    () => {
      mounted.current = true;
    },
    () => {
      mounted.current = false;
    }
  );

  const changeCoordinates = (coordinates: FinancialProductPreferenceCoordinates) => {
    if (!writableRef.current || saveInFlight.current) {
      return;
    }
    updateState({
      ...stateRef.current,
      drafts: {
        ...stateRef.current.drafts,
        [coordinates.productType]: coordinates,
      },
      saveError: null,
    });
  };

  const changeProduct = (productType: FinancialProductType) => {
    if (!writableRef.current || saveInFlight.current) {
      return;
    }
    updateState({...stateRef.current, selectedProduct: productType, saveError: null});
  };

  // A signed contract shows only its stored values. A poll at the open revision carries the current
  // pricing inputs (a configuration edit does not advance the revision); anything else keeps
  // pricing from the baseline the draft is based on.
  const pricingBody = match(props.products)
    .with({mode: 'CONTRACT_SNAPSHOT'}, (products) => products)
    .with({preferenceRevision: state.baseline.preferenceRevision}, (products) => products)
    .otherwise(() => state.baseline);
  const activeCoordinates = isNil(state.selectedProduct)
    ? undefined
    : state.drafts[state.selectedProduct];
  const activeOffer = isNil(state.selectedProduct)
    ? null
    : getFinancialProductOffer(pricingBody, state.selectedProduct);
  const activeDisplayed = isNil(activeCoordinates)
    ? NO_DISPLAYED_QUOTE
    : getDisplayedFinancialProductQuote({
        envelope: pricingBody,
        offer: activeOffer,
        coordinates: activeCoordinates,
      });
  // The selection the dialog opened with (re-seeded after a 409): saving it again would only bump
  // the revision and the seller attribution, so a product switch or a coordinate change is required.
  const hasChange =
    state.selectedProduct !==
      getPrimaryFinancialProductType(state.baseline, FINANCIAL_PRODUCT_ORDER) ||
    (!isNil(state.selectedProduct) &&
      !equals(activeCoordinates, draftsFromProducts(state.baseline)[state.selectedProduct]));
  // The seller saves only a selection whose price is on screen; the backend re-prices it anyway.
  const canSave =
    isWritable &&
    !isSaving &&
    !isNil(activeCoordinates) &&
    hasChange &&
    activeDisplayed.source !== 'NONE' &&
    activeDisplayed.quote.availability === 'AVAILABLE';

  const close = () => {
    if (saveInFlight.current) {
      return;
    }
    mounted.current = false;
    props.onClose();
  };

  const save = () => {
    if (!canSave || saveInFlight.current || !writableRef.current || !mounted.current) {
      return;
    }
    const current = stateRef.current;
    const coordinates = isNil(current.selectedProduct)
      ? undefined
      : current.drafts[current.selectedProduct];
    const parsed = FinancialProductPreferenceCoordinatesSchema.safeParse(coordinates);
    if (!parsed.success) {
      return;
    }
    saveInFlight.current = true;
    setIsSaving(true);
    updateState({...current, saveError: null});
    updatePreference({
      businessCaseId: props.businessCaseId,
      requestBody: toFinancialProductPreferenceRequestBody(
        parsed.data,
        current.baseline.preferenceRevision
      ),
    })
      .unwrap()
      .then(() => {
        saveInFlight.current = false;
        if (mounted.current) {
          setIsSaving(false);
          close();
        }
      })
      .catch((error: unknown) => {
        saveInFlight.current = false;
        if (!mounted.current) {
          return;
        }
        setIsSaving(false);
        const refusal = FinancialProductPreferenceRefusalSchema.safeParse(error);
        if (refusal.success) {
          const baseline = refusal.data.current;
          updateState({
            baseline,
            selectedProduct: getPrimaryFinancialProductType(baseline, FINANCIAL_PRODUCT_ORDER),
            drafts: draftsFromProducts(baseline),
            saveError: 'conflict',
            isTerminal:
              refusal.data.reason === 'CONTRACT_CLOSED' ||
              refusal.data.reason === 'NOT_IN_OFFER_STATE',
          });
        } else {
          updateState({...stateRef.current, saveError: 'failed'});
        }
      });
  };

  return {
    state,
    activeCoordinates,
    activeOffer,
    activeDisplayed,
    isWritable,
    isSaving,
    canSave,
    changeCoordinates,
    changeProduct,
    close,
    save,
    configuredProducts: getConfiguredFinancialProducts(state.baseline, FINANCIAL_PRODUCT_ORDER),
  };
}
